// frontend/src/composables/useNotificationSocket.ts
//
// Singleton WS-подписчик на /ws/notifications/.
//
// Бэк рассылает в одну и ту же группу `notifications_<user.id>` два разных
// payload-а (см. backend/PlatformaAPP/notifications/tasks.py и
// chats/viewset.py):
//   * чат-пинг      → { roomId, content, senderId, timestamp }
//   * платформенный → { public_id, event_type, kind, priority, title, body,
//                       status, recipient_role, resource_type, resource_id,
//                       created_at }
//
// Раньше у нас была одна сырая подписка в ChatsPage.vue, которая жёстко
// фильтровала всё «не-чатное» в пол. После того, как BE начал бродкастить
// PlatformNotification (commit afa6f47 / 76b5e84), колокол всё равно
// обновлялся раз в 30 секунд через polling.
//
// Этот композабл — один WebSocket на весь app-session: ChatsPage и колокол
// подписываются на `onMessage`, и каждый сам решает, какой shape ему нужен.
//
// Polling в NotificationComponent.vue ОСТАЁТСЯ как safety-net на случай,
// если WS оборвался / прокся гасит upgrade / cookie протух.
//
// Реализовано поверх существующего ChatSocket (services/chat-socket.ts),
// чтобы не плодить ещё один обёрток-класс. ChatSocket сам по себе НЕ умеет
// реконнекта с бэк-оффом — добавляем сверху.

import { onBeforeUnmount } from 'vue';
import { ChatSocket } from '@/services/chat-socket';
import { hasAuthFlag } from '@/services/authFlag';

export type SocketMessage = Record<string, unknown>;
export type SocketListener = (payload: SocketMessage) => void;
export type SocketReconnectListener = () => void;
export type SocketDegradedListener = (degraded: boolean) => void;

const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 60_000;
const RECONNECT_FACTOR = 2;

// N1 — стоп-кран реконнекта. За 18.08 прод-логи насчитали 5 627 отказов 403 на
// /ws/notifications/ против 648 успешных 101, из них 5 332 — с одной машины:
// при полуживой сессии клиент долбился в апгрейд часами. Бэкофф сам по себе
// шторм не гасит, потому что счётчик обнуляется на каждом 'jwt-refreshed'.
// После этого числа отказов ПОДРЯД перестаём пробовать вовсе — до смены
// auth-состояния (успешный refresh/логин).
// 1+2+4+8+16+32+60 ≈ 2 минуты попыток, дальше тишина.
const MAX_RECONNECT_ATTEMPTS = 7;

// L1 — если сокет не дошёл до 'open' за этот дедлайн, считаем WS заблокированным
// (прокся/AV гасит upgrade) и поднимаем degraded → колокол ужимает polling.
const OPEN_DEADLINE_MS = 8_000;

// Резолвим WS_ROOT тем же способом, что ChatsPage.vue — иначе риск разойтись
// на dev/staging/prod. Не выносим в shared utility, потому что на момент
// этого фикса единственный другой потребитель — ChatsPage.vue (refactor в
// одно общее место можно сделать отдельным проходом, см. отчёт).
function pickEnv(...vars: string[]): string | undefined {
    for (const v of vars) {
        // eslint-disable-next-line no-prototype-builtins
        if (typeof process !== 'undefined' && process.env?.hasOwnProperty(v)) {
            return (process as any).env[v];
        }
        if (typeof import.meta !== 'undefined' && (import.meta as any).env?.[v]) {
            return (import.meta as any).env[v];
        }
    }
    return undefined;
}

function resolveWsRoot(): string {
    const explicit = pickEnv('VITE_WS_ROOT', 'VUE_APP_WS_ROOT');
    if (explicit) return explicit;
    if (typeof window === 'undefined') return '';
    return window.location.origin.replace(/^http/i, 'ws');
}

// ── module-scoped state (singleton) ────────────────────────────────────
let socket: ChatSocket | null = null;
const listeners = new Set<SocketListener>();
const reconnectListeners = new Set<SocketReconnectListener>();
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let jwtRefreshBound = false;
// Стоп-кран сработал: попыток больше не делаем до смены auth-состояния.
let reconnectSuspended = false;

// L1 — здоровье транспорта. degraded=true, когда сокет не открывается (заблокирован).
const degradedListeners = new Set<SocketDegradedListener>();
let degraded = false;
let openDeadlineTimer: ReturnType<typeof setTimeout> | null = null;

function clearReconnectTimer() {
    if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
}

function notifyDegraded() {
    for (const cb of degradedListeners) {
        try {
            cb(degraded);
        } catch (err) {
            console.error('[useNotificationSocket] degraded listener threw', err);
        }
    }
}

function setDegraded(value: boolean) {
    if (degraded === value) return;
    degraded = value;
    notifyDegraded();
}

function clearOpenDeadline() {
    if (openDeadlineTimer !== null) {
        clearTimeout(openDeadlineTimer);
        openDeadlineTimer = null;
    }
}

function startOpenDeadline() {
    clearOpenDeadline();
    openDeadlineTimer = setTimeout(() => {
        openDeadlineTimer = null;
        // Сокет не открылся за дедлайн → WS, похоже, режут на пути до бэка.
        // Поднимаем degraded и роняем подвисший коннект, уходя в backoff-
        // реконнект (вдруг затык временный). degraded снимется только на
        // реальном 'open'.
        setDegraded(true);
        dropSocket();
    }, OPEN_DEADLINE_MS);
}

/**
 * Снести текущий коннект по нашей инициативе и уйти в backoff-реконнект.
 *
 * Именно «по нашей»: ChatSocket.disconnect() на неоткрытом сокете НЕ роняет
 * 'close'-событие (close() посреди хендшейка = красная строка в консоли), так
 * что ждать его здесь бессмысленно — состояние синглтона двигаем сами.
 */
function dropSocket() {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
    if (listeners.size > 0 || reconnectListeners.size > 0) {
        scheduleReconnect();
    }
}

function scheduleReconnect() {
    clearReconnectTimer();

    // Сессии нет — WS не нужен и всё равно получит 403. Ждём логина.
    if (!hasAuthFlag()) {
        reconnectSuspended = true;
        setDegraded(true);
        return;
    }

    if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
        if (!reconnectSuspended) {
            reconnectSuspended = true;
            // degraded=true оставляет колокол на учащённом polling'е — доставка
            // уведомлений продолжается, шторма коннектов нет.
            setDegraded(true);
            console.warn(
                `[useNotificationSocket] ${reconnectAttempt} отказов подряд — реконнект остановлен до смены auth-состояния`,
            );
        }
        return;
    }

    const delay = Math.min(
        RECONNECT_INITIAL_MS * Math.pow(RECONNECT_FACTOR, reconnectAttempt),
        RECONNECT_MAX_MS,
    );
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        openSocket();
    }, delay);
}

/**
 * Смена auth-состояния — единственное, что снимает стоп-кран: куки обновились,
 * значит следующий handshake имеет шанс пройти.
 */
function resumeReconnect() {
    reconnectAttempt = 0;
    reconnectSuspended = false;
    clearReconnectTimer();
    if (socket) {
        socket.reconnect();
        return;
    }
    if (listeners.size > 0 || reconnectListeners.size > 0) {
        openSocket();
    }
}

function dispatch(payload: SocketMessage) {
    for (const cb of listeners) {
        try {
            cb(payload);
        } catch (err) {
            console.error('[useNotificationSocket] listener threw', err);
        }
    }
}

function notifyReconnect() {
    for (const cb of reconnectListeners) {
        try {
            cb();
        } catch (err) {
            console.error('[useNotificationSocket] reconnect listener threw', err);
        }
    }
}

function bindJwtRefreshOnce() {
    if (jwtRefreshBound) return;
    if (typeof window === 'undefined') return;
    jwtRefreshBound = true;
    // куки обновились — насильно дёрнем сокет, чтобы Channels прочитал свежую
    // сессию, и снимем стоп-кран, если он успел сработать.
    window.addEventListener('jwt-refreshed', resumeReconnect);
}

function openSocket() {
    if (typeof window === 'undefined') return;
    // Слушателя вешаем ДО гарда на сессию: иначе при погашенном флаге он бы
    // никогда не привязался и логин не разбудил бы сокет.
    bindJwtRefreshOnce();
    if (socket) return;

    // Без флага сессии handshake гарантированно вернёт 403 — не ходим вовсе.
    if (!hasAuthFlag()) {
        reconnectSuspended = true;
        return;
    }

    const url = `${resolveWsRoot()}/ws/notifications/`;
    socket = new ChatSocket(url);

    socket.on('message', (payload: unknown) => {
        if (payload && typeof payload === 'object') {
            dispatch(payload as SocketMessage);
        }
    });

    socket.on('open', () => {
        // успешный коннект — сбрасываем backoff. Если это был re-open
        // (после обрыва), даём подписчикам шанс подтянуть упущенное.
        const wasReconnect = reconnectAttempt > 0;
        reconnectAttempt = 0;
        reconnectSuspended = false;
        clearReconnectTimer();
        clearOpenDeadline();
        setDegraded(false); // реальный коннект → транспорт здоров
        if (wasReconnect) {
            notifyReconnect();
        }
    });

    socket.on('close', () => {
        // ChatSocket.disconnect() обнуляет ws изнутри; здесь мы только
        // фиксируем, что коннект пропал — наш singleton-ref должен это
        // отразить, иначе следующий openSocket() ничего не сделает.
        socket = null;
        if (listeners.size > 0 || reconnectListeners.size > 0) {
            scheduleReconnect();
        }
    });

    socket.connect();
    startOpenDeadline();
}

function maybeCloseSocket() {
    if (
        listeners.size > 0 ||
        reconnectListeners.size > 0 ||
        degradedListeners.size > 0
    ) {
        return;
    }
    clearReconnectTimer();
    clearOpenDeadline();
    reconnectAttempt = 0;
    reconnectSuspended = false;
    setDegraded(false);
    if (socket) {
        socket.disconnect();
        socket = null;
    }
}

// ── public API ─────────────────────────────────────────────────────────

export interface UseNotificationSocketOptions {
    /** Автоматически снимать подписку в onBeforeUnmount (default: true). */
    autoUnsubscribe?: boolean;
}

export interface UseNotificationSocketReturn {
    /** Подписаться на каждое входящее сообщение. Возвращает функцию отписки. */
    onMessage: (cb: SocketListener) => () => void;
    /** Подписаться на событие «коннект восстановлен». */
    onReconnect: (cb: SocketReconnectListener) => () => void;
    /**
     * Подписаться на смену здоровья WS: degraded=true → транспорт не встаёт
     * (прокся/AV режут upgrade). cb сразу вызывается с текущим состоянием.
     * НЕ открывает сокет сам (мониторинг здоровья без активного потребителя
     * бессмыслен) — используется в паре с onMessage.
     */
    onDegradedChange: (cb: SocketDegradedListener) => () => void;
    /** Принудительно открыть коннект (вызывается автоматически из onMessage). */
    connect: () => void;
    /** Снять все подписки этого экземпляра. */
    disposeAll: () => void;
}

export function useNotificationSocket(
    options: UseNotificationSocketOptions = {},
): UseNotificationSocketReturn {
    const { autoUnsubscribe = true } = options;
    const ownListeners = new Set<SocketListener>();
    const ownReconnectListeners = new Set<SocketReconnectListener>();
    const ownDegradedListeners = new Set<SocketDegradedListener>();

    const onMessage = (cb: SocketListener) => {
        listeners.add(cb);
        ownListeners.add(cb);
        openSocket();
        return () => {
            listeners.delete(cb);
            ownListeners.delete(cb);
            maybeCloseSocket();
        };
    };

    const onReconnect = (cb: SocketReconnectListener) => {
        reconnectListeners.add(cb);
        ownReconnectListeners.add(cb);
        openSocket();
        return () => {
            reconnectListeners.delete(cb);
            ownReconnectListeners.delete(cb);
            maybeCloseSocket();
        };
    };

    // NB: не открывает сокет (в отличие от onMessage/onReconnect) — мониторинг
    // здоровья без активного потребителя сообщений смысла не имеет.
    const onDegradedChange = (cb: SocketDegradedListener) => {
        degradedListeners.add(cb);
        ownDegradedListeners.add(cb);
        cb(degraded); // немедленно отдаём текущее состояние
        return () => {
            degradedListeners.delete(cb);
            ownDegradedListeners.delete(cb);
            maybeCloseSocket();
        };
    };

    const connect = () => openSocket();

    const disposeAll = () => {
        for (const cb of ownListeners) listeners.delete(cb);
        ownListeners.clear();
        for (const cb of ownReconnectListeners) reconnectListeners.delete(cb);
        ownReconnectListeners.clear();
        for (const cb of ownDegradedListeners) degradedListeners.delete(cb);
        ownDegradedListeners.clear();
        maybeCloseSocket();
    };

    if (autoUnsubscribe) {
        try {
            // onBeforeUnmount бросит warning, если вызван вне setup()-контекста.
            // Сценарий «вне компонента» допустим (например, тесты), поэтому
            // подавляем тихо.
            onBeforeUnmount(disposeAll);
        } catch {
            /* not inside a component instance — caller must dispose manually */
        }
    }

    return { onMessage, onReconnect, onDegradedChange, connect, disposeAll };
}

// ── test helpers (НЕ для прод-кода) ────────────────────────────────────
// Vitest гоняет несколько тестовых файлов в одном модульном кэше, и наш
// singleton state течёт между тестами. Экспортируем reset-хук, чтобы тесты
// могли явно его дёрнуть. В прод-сборку этот символ попадёт, но никто его
// не зовёт.
export function __resetNotificationSocketForTests(): void {
    listeners.clear();
    reconnectListeners.clear();
    degradedListeners.clear();
    clearReconnectTimer();
    clearOpenDeadline();
    reconnectAttempt = 0;
    reconnectSuspended = false;
    degraded = false;
    if (socket) {
        socket.disconnect();
        socket = null;
    }
}
